// ============================================================================
// Arbres d'expressions : lecture, calcul, écriture, complexité, équivalence,
// appariement. Fonctions pures (aucun état d'exercice). Porté du prototype.
//
// Les nœuds gardent la forme compacte du prototype ({ k, v, n, c, a, b }) :
// champs facultatifs selon le genre. Les accès `!` reflètent les invariants
// de chaque genre (un 'add' a toujours `c`, un 'div' a `a` et `b`…).
// ============================================================================

import {
  CONSTS,
  formatNumber,
  isCmp,
  isConst,
  isFunc,
  isMod,
  isNum,
  isPow,
  isSupPow,
  isVar,
  lex,
  modValue,
  normalize,
  supPowValue,
} from './lexer';

/** Fonctions usuelles (moteur v2) : un nœud unaire par fonction. */
export type FuncKind =
  | 'ln'
  | 'exp'
  | 'cos'
  | 'sin'
  | 'tan'
  | 'arg'
  // moteur v17 : réciproques, résultat en DEGRÉS
  | 'arccos'
  | 'arcsin'
  | 'arctan'
  // moteur v45 : logarithme décimal
  | 'log';
export type NodeKind =
  | 'n'
  | 's'
  | 'add'
  | 'mul'
  | 'div'
  | 'pow'
  | 'neg'
  | 'sq'
  | 'sqrt'
  | 'pct'
  /** angle en degrés (moteur v2) : 60° = π ÷ 3 */
  | 'deg'
  /** factorielle (moteur v6) : 5! = 120 */
  | 'fact'
  /** module / valeur absolue (moteur v11) : |z|, |−3| */
  | 'abs'
  /** « k parmi n » (moteur v27) : a = k (choisis), b = n (ensemble) */
  | 'binom'
  | FuncKind;
const FUNC_KINDS: Record<string, 1> = {
  ln: 1,
  exp: 1,
  cos: 1,
  sin: 1,
  tan: 1,
  arg: 1,
  arccos: 1,
  arcsin: 1,
  arctan: 1,
};
export function isFuncNode(t: Node): boolean {
  return FUNC_KINDS[t.k] === 1;
}
export interface Node {
  k: NodeKind;
  /** valeur d'un nombre (k = 'n') */
  v?: number;
  /** nom d'une grandeur (k = 's') */
  n?: string;
  /** enfants d'une somme ou d'un produit */
  c?: Node[];
  /** opérande (unaire) ou membre gauche (div, pow) */
  a?: Node;
  /** membre droit (div, pow) */
  b?: Node;
}
/** Une égalité : des membres reliés par « = » (2 en général, plus pour une chaîne). */
export interface Eq {
  sides: Node[];
  /**
   * Congruences (moteur v3) : relation entre deux membres consécutifs, « = »
   * ou « ≡ » (absent : que des « = »). `rels[i]` relie sides[i] et sides[i + 1].
   */
  rels?: ('=' | '≡')[];
  /** modulo d'une congruence, écrit à la fin entre crochets : « 23 ≡ 3 [5] » */
  mod?: number;
  /** comparaison (moteur v5) : sides[0] cmp sides[1] (« Δ > 0 ») — jamais une égalité */
  cmp?: '>' | '<' | '≥' | '≤' | '≈';
  /**
   * comparaisons EN CHAÎNE (moteur v23, feuilles du noyau seulement) : « −1 < x ≤ 2 ».
   * `chain[i]` relie sides[i] et sides[i + 1] ; toutes dans le même sens (< et ≤, ou
   * > et ≥). Jamais avec `cmp`, `rels` ni un modulo.
   */
  chain?: ChainRel[];
}
export type ChainRel = '<' | '>' | '≤' | '≥';
/** Une ligne : des alternatives reliées par « ou ». */
export interface Line {
  dis: Eq[];
}
export type Env = Record<string, number>;

export const SEP = '\u0001';

/* ================= arbres ================= */
export function num(v: number): Node {
  return { k: 'n', v };
}
export function sym(n: string): Node {
  return { k: 's', n };
}
export function nary(k: NodeKind, c: Node[]): Node {
  let out: Node[] = [];
  c.forEach((x) => {
    if (x.k === k) out = out.concat(x.c!);
    else out.push(x);
  });
  return out.length === 1 ? out[0]! : { k, c: out };
}
export function neg(a: Node): Node {
  if (a.k === 'n') return num(-a.v!);
  if (a.k === 'neg') return a.a!;
  return { k: 'neg', a };
}
export function un(k: NodeKind, a: Node): Node {
  return { k, a };
}
export function bin(k: NodeKind, a: Node, b: Node): Node {
  return { k, a, b };
}
export function dv(a: Node, b: Node): Node {
  return bin('div', a, b);
}
export function pw(a: Node, b: Node): Node {
  return bin('pow', a, b);
}
export function isBin(t: Node): boolean {
  return t.k === 'div' || t.k === 'pow' || t.k === 'binom';
}
export function kids(t: Node): Node[] {
  return t.c ? t.c : isBin(t) ? [t.a!, t.b!] : t.a ? [t.a] : [];
}
export function mapKids(t: Node, f: (c: Node) => Node): Node {
  if (t.c) return nary(t.k, t.c.map(f));
  if (isBin(t)) return bin(t.k, f(t.a!), f(t.b!));
  if (t.k === 'neg') return neg(f(t.a!));
  if (t.a) return un(t.k, f(t.a));
  return t;
}

/* ================= lecture ================= */
const SUPMAP: Record<string, string> = {
  '⁰': '0',
  '¹': '1',
  '²': '2',
  '³': '3',
  '⁴': '4',
  '⁵': '5',
  '⁶': '6',
  '⁷': '7',
  '⁸': '8',
  '⁹': '9',
  '⁺': '+',
  '⁻': '−',
  ⁿ: 'n',
};
const SUPINV: Record<string, string> = {};
Object.keys(SUPMAP).forEach((k) => {
  SUPINV[SUPMAP[k]!] = k;
});

function splitAt(atoms: string[], sep: string): string[][] {
  const parts: string[][] = [[]];
  let depth = 0;
  atoms.forEach((a) => {
    if (a === '(') depth++;
    if (a === ')') depth--;
    if (depth === 0 && a === sep) parts.push([]);
    else parts[parts.length - 1]!.push(a);
  });
  return parts;
}
function parsePow(a: string): Node {
  const m = /^(\d+)(.+)$/.exec(a)!;
  const exp = m[2]!
    .split('')
    .map((c) => SUPMAP[c] || '')
    .join('');
  return pw(num(Number(m[1])), parseExpr(normalize(lex(exp))));
}
export function parseExpr(atoms: string[]): Node {
  let pos = 0;
  const peek = (): string | undefined => atoms[pos];
  const next = (): string | undefined => atoms[pos++];
  function sum(): Node {
    const terms = [term()];
    while (peek() === '+' || peek() === '−') {
      const op = next();
      const t = term();
      terms.push(op === '−' ? neg(t) : t);
    }
    return nary('add', terms);
  }
  function term(): Node {
    let node = parmi();
    while (peek() === '×' || peek() === '÷') {
      const op = next();
      const r = parmi();
      node = op === '×' ? nary('mul', [node, r]) : dv(node, r);
    }
    return node;
  }
  // « k parmi n » (moteur v27) : plus fort que × et ÷, moins fort que le signe et les
  // exposants ; « 2 parmi 4 parmi 6 » se lit (2 parmi 4) parmi 6
  function parmi(): Node {
    let node = unary();
    while (peek() === 'parmi') {
      next();
      node = bin('binom', node, unary());
    }
    return node;
  }
  function unary(): Node {
    if (peek() === '−') {
      next();
      return neg(unary());
    }
    if (peek() === '+') {
      next();
      return unary();
    }
    return postfix();
  }
  function postfix(): Node {
    let n = primary();
    for (;;) {
      if (peek() === '²' || peek() === '%') n = un(next() === '²' ? 'sq' : 'pct', n);
      else if (peek() === '°') {
        next();
        n = un('deg', n);
      } else if (peek() === '!') {
        next();
        n = un('fact', n);
      } else if (peek() === '^') {
        // puissance générale (moteur v2), associative à droite : 2^3^2 = 2^(3^2)
        next();
        n = pw(n, unary());
      } else if (isSupPow(peek())) {
        // moteur v41 : « x³ », « (2x)³ »
        n = pw(n, num(supPowValue(next()!)));
      } else break;
    }
    return n;
  }
  function primary(): Node {
    const a = next();
    if (a === undefined) throw new Error('fin');
    if (isPow(a)) return parsePow(a);
    if (isNum(a)) return num(Number(a.replace(',', '.')));
    if (isVar(a) || isConst(a)) return sym(a);
    if (isFunc(a)) {
      // ln(5), cos(x), exp(−x) : la fonction s'applique à l'opérande qui suit
      if (peek() === '−') {
        next();
        return un(a as FuncKind, neg(primary()));
      }
      return un(a as FuncKind, primary());
    }
    if (a === '(') {
      const n = sum();
      if (next() !== ')') throw new Error('parenthèse');
      return n;
    }
    if (a === '√') return un('sqrt', postfix());
    // module (moteur v11) : « |z| », « |3 + 4i| »
    if (a === '|') {
      const n = sum();
      if (next() !== '|') throw new Error('barre');
      return un('abs', n);
    }
    throw new Error('inattendu');
  }
  const t = sum();
  if (pos !== atoms.length) throw new Error('reste');
  return t;
}
/**
 * une ligne = des alternatives (« ou ») ; chacune = des membres reliés par « = ».
 * `chains` (moteur v23, feuilles du noyau) : une alternative peut être une chaîne de
 * comparaisons « −1 < x ≤ 2 » ; sans l'option, deux comparaisons sont refusées.
 */
export function parseLine(atoms: string[], opts?: { chains?: boolean }): Line {
  return { dis: splitAt(atoms, 'ou').map((d) => parseEq(d, !!opts?.chains)) };
}
const LOWER: Record<string, 1> = { '<': 1, '≤': 1 };
const UPPER: Record<string, 1> = { '>': 1, '≥': 1 };
function parseEq(d: string[], chains = false): Eq {
  const last = d[d.length - 1];
  const mod = last !== undefined && isMod(last) ? modValue(last) : undefined;
  const body = mod === undefined ? d : d.slice(0, -1);
  const ci = topIndex(body, isCmp);
  if (ci >= 0) {
    // comparaison (moteur v5) : deux membres, une seule relation
    if (mod !== undefined || body.indexOf('=') >= 0 || body.indexOf('≡') >= 0)
      throw new Error('comparaison mêlée à une égalité');
    if (topIndex(body.slice(ci + 1), isCmp) >= 0) {
      if (!chains) throw new Error('deux comparaisons');
      return parseChain(body);
    }
    return {
      sides: [parseExpr(body.slice(0, ci)), parseExpr(body.slice(ci + 1))],
      cmp: body[ci] as Eq['cmp'],
    };
  }
  if (mod === undefined && body.indexOf('≡') < 0)
    return { sides: splitAt(body, '=').map(parseExpr) };
  // congruence : membres reliés par « = » ou « ≡ », modulo à la fin
  const sides: string[][] = [[]];
  const rels: ('=' | '≡')[] = [];
  let depth = 0;
  body.forEach((a) => {
    if (a === '(') depth++;
    if (a === ')') depth--;
    if (depth === 0 && (a === '=' || a === '≡')) {
      rels.push(a);
      sides.push([]);
    } else sides[sides.length - 1]!.push(a);
  });
  const eq: Eq = { sides: sides.map(parseExpr), rels };
  if (mod !== undefined) eq.mod = mod;
  return eq;
}
/** « −1 < x ≤ 2 » : membres reliés par des comparaisons du même sens (moteur v23) */
function parseChain(body: string[]): Eq {
  const sides: string[][] = [[]];
  const chain: ChainRel[] = [];
  let depth = 0;
  body.forEach((a) => {
    if (a === '(') depth++;
    if (a === ')') depth--;
    if (depth === 0 && isCmp(a)) {
      if (a === '≈') throw new Error('« ≈ » dans une chaîne');
      chain.push(a as ChainRel);
      sides.push([]);
    } else sides[sides.length - 1]!.push(a);
  });
  const lower = chain.every((r) => LOWER[r]);
  const upper = chain.every((r) => UPPER[r]);
  if (!lower && !upper) throw new Error('chaîne dans les deux sens');
  return { sides: sides.map(parseExpr), chain };
}
/** n! pour un entier naturel n ≤ 170 (au-delà : infini), sinon NaN */
export function factorial(n: number): number {
  if (!Number.isFinite(n) || n < 0 || Math.abs(n - Math.round(n)) > 1e-9) return NaN;
  let r = 1;
  for (let i = 2; i <= Math.round(n); i++) r *= i;
  return r;
}
/**
 * « k parmi n » (moteur v27) : n! ÷ (k! × (n − k)!) pour des entiers naturels, 0 si
 * k > n ; NaN sinon. Calculé en produit (n − k + 1) … n ÷ k! pour rester exact.
 */
export function binomial(n: number, k: number): number {
  const isNat = (v: number): boolean =>
    Number.isFinite(v) && v >= -1e-9 && Math.abs(v - Math.round(v)) <= 1e-9;
  if (!isNat(n) || !isNat(k)) return NaN;
  const N = Math.round(n);
  let K = Math.round(k);
  if (K > N) return 0;
  K = Math.min(K, N - K);
  let r = 1;
  for (let i = 1; i <= K; i++) r = (r * (N - K + i)) / i;
  return r > 2 ** 53 ? r : Math.round(r);
}
/** position du premier atome (hors parenthèses) qui vérifie `test`, sinon −1 */
function topIndex(atoms: string[], test: (a: string) => boolean): number {
  let depth = 0;
  for (let i = 0; i < atoms.length; i++) {
    const a = atoms[i]!;
    if (a === '(') depth++;
    else if (a === ')') depth--;
    else if (depth === 0 && test(a)) return i;
  }
  return -1;
}
/** vraie la comparaison a cmp b ? (égalité à la précision du moteur pour ≥ et ≤) */
export function cmpHolds(cmp: NonNullable<Eq['cmp']>, a: number, b: number): boolean {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  const eq = close(a, b);
  switch (cmp) {
    case '>':
      return a > b && !eq;
    case '<':
      return a < b && !eq;
    case '≥':
      return a > b || eq;
    case '≤':
      return a < b || eq;
    case '≈':
      // arrondi (moteur v10) : à une demi-unité du dernier chiffre écrit de b
      return Math.abs(a - b) <= 0.5 * 10 ** -decimalPlaces(b) + 1e-9;
  }
}
/** nombre de chiffres après la virgule d'un décimal (au plus 6) */
export function decimalPlaces(v: number): number {
  for (let p = 0; p <= 6; p++) if (Math.abs(Math.round(v * 10 ** p) / 10 ** p - v) < 1e-9) return p;
  return 6;
}
/** v arrondi à p chiffres après la virgule (au plus proche, 0,5 vers le haut) */
export function roundTo(v: number, p: number): number {
  const f = 10 ** p;
  return Math.round((v + Math.sign(v) * 1e-12) * f) / f;
}
/** v coupé (tronqué) à p chiffres après la virgule */
export function truncTo(v: number, p: number): number {
  const f = 10 ** p;
  return Math.trunc((v + Math.sign(v) * 1e-12) * f) / f;
}
/**
 * Texte d'une ligne écrite par un auteur (modèle, corrigé, choix) → ligne. Les chaînes de
 * comparaisons (« −1 < x ≤ 2 », moteur v23) y sont lues : seules les feuilles du noyau
 * en écrivent, la porte refuse ailleurs ce que l'ancien juge ne lirait pas.
 */
export function parseText(text: string): Line {
  return parseLine(normalize(lex(text)), { chains: true });
}
export function eq2(a: Node, b: Node): Line {
  return { dis: [{ sides: [a, b] }] };
}

/* ================= calcul ================= */
export function ev(t: Node, env: Env): number {
  switch (t.k) {
    case 'n':
      return t.v!;
    case 's':
      if (env[t.n!] !== undefined) return env[t.n!]!;
      return CONSTS[t.n!] ?? NaN;
    case 'add':
      return t.c!.reduce((s, c) => s + ev(c, env), 0);
    case 'mul':
      return t.c!.reduce((s, c) => s * ev(c, env), 1);
    case 'div': {
      const b = ev(t.b!, env);
      return b === 0 ? NaN : ev(t.a!, env) / b;
    }
    case 'pow':
      return realPow(ev(t.a!, env), ev(t.b!, env));
    case 'neg':
      return -ev(t.a!, env);
    case 'sq': {
      const a = ev(t.a!, env);
      return a * a;
    }
    case 'sqrt': {
      const r = ev(t.a!, env);
      return r < 0 ? NaN : Math.sqrt(r);
    }
    case 'pct':
      return ev(t.a!, env) / 100;
    case 'deg':
      return (ev(t.a!, env) * Math.PI) / 180;
    case 'fact':
      return factorial(ev(t.a!, env));
    case 'binom':
      return binomial(ev(t.b!, env), ev(t.a!, env));
    case 'ln': {
      const a = ev(t.a!, env);
      return a > 0 ? Math.log(a) : NaN;
    }
    case 'log': {
      const a = ev(t.a!, env);
      return a > 0 ? Math.log10(a) : NaN;
    }
    case 'exp':
      return Math.exp(ev(t.a!, env));
    case 'cos':
      return Math.cos(ev(t.a!, env));
    case 'sin':
      return Math.sin(ev(t.a!, env));
    case 'tan': {
      const a = ev(t.a!, env);
      return Math.abs(Math.cos(a)) < 1e-12 ? NaN : Math.tan(a);
    }
    case 'abs':
      return Math.abs(ev(t.a!, env));
    // moteur v17 : angle en degrés (collège), hors de [−1 ; 1] : pas d'angle
    case 'arccos': {
      const a = ev(t.a!, env);
      return a >= -1 - 1e-12 && a <= 1 + 1e-12
        ? (Math.acos(Math.max(-1, Math.min(1, a))) * 180) / Math.PI
        : NaN;
    }
    case 'arcsin': {
      const a = ev(t.a!, env);
      return a >= -1 - 1e-12 && a <= 1 + 1e-12
        ? (Math.asin(Math.max(-1, Math.min(1, a))) * 180) / Math.PI
        : NaN;
    }
    case 'arctan':
      return (Math.atan(ev(t.a!, env)) * 180) / Math.PI;
    case 'arg': {
      // argument d'un réel (moteur v11) : 0 s'il est positif, π s'il est négatif
      const a = ev(t.a!, env);
      return a > 0 ? 0 : a < 0 ? Math.PI : NaN;
    }
  }
  return NaN;
}
export function close(a: number, b: number): boolean {
  return (
    isFinite(a) && isFinite(b) && Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b))
  );
}
export function symsOf(t: Node, acc?: Record<string, 1>): Record<string, 1> {
  const out = acc || {};
  // une constante (π) n'est jamais une grandeur à connaître
  if (t.k === 's' && !isConst(t.n)) out[t.n!] = 1;
  kids(t).forEach((c) => symsOf(c, out));
  return out;
}
export function lineSyms(line: Line): Record<string, 1> {
  const acc: Record<string, 1> = {};
  line.dis.forEach((d) => d.sides.forEach((s) => symsOf(s, acc)));
  return acc;
}
export function numericIn(t: Node, K: Env): boolean {
  return Object.keys(symsOf(t)).every((s) => K[s] !== undefined);
}
export function numVal(t: Node, K: Env): number | null {
  if (!numericIn(t, K)) return null;
  const v = ev(t, K);
  return isFinite(v) ? v : null;
}
export function hasUnknown(t: Node, K: Env): boolean {
  return !numericIn(t, K);
}
export function hasUnknownLine(line: Line, K: Env): boolean {
  return Object.keys(lineSyms(line)).some((s) => K[s] === undefined);
}
export function combine(k: NodeKind, list: Node[], K: Env): number | null {
  const vals = list.map((c) => numVal(c, K));
  if (vals.some((v) => v === null)) return null;
  return k === 'add'
    ? (vals as number[]).reduce((a, b) => a + b, 0)
    : (vals as number[]).reduce((a, b) => a * b, 1);
}

/* ================= forme canonique, écriture ================= */
export function canon(t: Node): string {
  if (t.k === 'n') return formatNumber(t.v!);
  if (t.k === 's') return t.n!;
  if (t.c) return t.k + '(' + t.c.map(canon).sort().join(',') + ')';
  if (isBin(t)) return t.k + '(' + canon(t.a!) + ',' + canon(t.b!) + ')';
  return t.k + '(' + canon(t.a!) + ')';
}
/**
 * La valeur s'écrit-elle exactement en décimal (au plus 4 chiffres après la
 * virgule, comme `formatNumber`) ? 1/3, √2, ln(5), π : non — on les garde
 * écrits (moteur v2), sinon l'arrondi rendrait une ligne fausse.
 */
export function isExactDecimal(v: number): boolean {
  if (!isFinite(v)) return false;
  const back = Number(formatNumber(v).replace('−', '-').replace(',', '.'));
  // tolérance d'arrondi flottant seulement : 500 × e² = 3694,5280494… n'est pas un décimal
  // à 6 chiffres (une tolérance relative de 1e-9 × |v| l'acceptait)
  return Math.abs(back - v) <= Math.max(1e-10, 1e-13 * Math.abs(v));
}
/**
 * a^b réel (moteur v12) : une base négative avec un exposant p/q d'indice q IMPAIR
 * a une valeur réelle — (−27)^(1 ÷ 3) = −3, (−8)^(2 ÷ 3) = 4 ; sinon NaN (Math.pow).
 */
export function realPow(a: number, b: number): number {
  if (a >= 0 || Number.isInteger(b) || !isFinite(b)) return Math.pow(a, b);
  const f = asFraction(b);
  if (!f || f[1] % 2 === 0) return NaN;
  const r = Math.pow(-a, b);
  return Math.abs(f[0]) % 2 === 1 ? -r : r;
}
/** v = p/q avec q ≤ 1000 (1/6, −2/3) : la fraction, sinon null. */
export function asFraction(v: number): [number, number] | null {
  if (!isFinite(v)) return null;
  for (let q = 2; q <= 1000; q++) {
    const p = Math.round(v * q);
    if (Math.abs(p / q - v) < 1e-12 * Math.max(1, Math.abs(v))) return [p, q];
  }
  return null;
}
export function subst(t: Node, K: Env, keepLone?: boolean, closed?: Record<string, Node>): Node {
  if (t.k === 's') {
    const v = K[t.n!];
    if (v === undefined || keepLone) return t;
    if (isExactDecimal(v)) return num(v);
    // valeur fractionnaire (1/6) : remplacée par la fraction ; irrationnelle :
    // par sa forme exacte déjà écrite (AB = √32), sinon gardée
    const f = asFraction(v);
    if (f) return dv(num(f[0]), num(f[1]));
    return closed?.[t.n!] ?? t;
  }
  return mapKids(t, (c) => subst(c, K, false, closed));
}
export function substLine(line: Line, K: Env): Line {
  return { dis: line.dis.map((d) => ({ sides: d.sides.map((s) => subst(s, K)) })) };
}
export function replaceSym(t: Node, name: string, expr: Node): Node {
  if (t.k === 's') return t.n === name ? expr : t;
  return mapKids(t, (c) => replaceSym(c, name, expr));
}
export function isAtomic(t: Node): boolean {
  return (t.k === 'n' && t.v! >= 0) || t.k === 's' || t.k === 'abs' || !!powAtom(t);
}
function numAtoms(v: number): string[] {
  const s = formatNumber(Math.abs(v));
  return v < 0 ? ['−', s] : [s];
}
/** 2ⁿ⁺¹ s'écrit d'un seul bout */
export function powAtom(t: Node): string | null {
  if (t.k !== 'pow' || t.a!.k !== 'n' || t.a!.v! < 0 || !Number.isInteger(t.a!.v)) return null;
  const b = t.b!;
  let e: string | null = null;
  if (b.k === 'n' && b.v! >= 0 && Number.isInteger(b.v)) e = String(b.v);
  else if (b.k === 's' && b.n!.length === 1) e = b.n!;
  else if (
    b.k === 'add' &&
    b.c!.length === 2 &&
    b.c![0]!.k === 's' &&
    b.c![0]!.n!.length === 1 &&
    b.c![1]!.k === 'n' &&
    Number.isInteger(b.c![1]!.v)
  ) {
    e = b.c![0]!.n! + (b.c![1]!.v! < 0 ? '−' : '+') + Math.abs(b.c![1]!.v!);
  }
  if (e === null) return null;
  const sup = e.split('').map((c) => SUPINV[c]);
  if (sup.some((c) => !c)) return null;
  return String(t.a!.v) + sup.join('');
}
function wrap(t: Node, paren: boolean): string[] {
  const a = toAtoms(t);
  return paren ? ['('].concat(a).concat([')']) : a;
}
/** ce qui suit un « − » : entre parenthèses si c'est une somme, ou si l'écriture
 * commencerait elle-même par un « − » (« 2 × 3 − (−1) × (−6) », jamais « − −1 ») */
function negOperand(t: Node): string[] {
  // (−1) × (−6) : seul le facteur négatif de tête prend des parenthèses
  if (t.k === 'mul' && t.c![0]!.k === 'n' && t.c![0]!.v! < 0) {
    const rest = t.c!.slice(1);
    return ['(']
      .concat(toAtoms(t.c![0]!))
      .concat([')', '×'])
      .concat(
        rest.length === 1
          ? wrap(
              rest[0]!,
              rest[0]!.k === 'add' ||
                rest[0]!.k === 'neg' ||
                (rest[0]!.k === 'n' && rest[0]!.v! < 0),
            )
          : toAtoms(nary('mul', rest)),
      );
  }
  const a = toAtoms(t);
  return t.k === 'add' || t.k === 'binom' || a[0] === '−' ? ['('].concat(a).concat([')']) : a;
}
export function toAtoms(t: Node): string[] {
  switch (t.k) {
    case 'n':
      return numAtoms(t.v!);
    case 's':
      return [t.n!];
    case 'add': {
      let out: string[] = [];
      t.c!.forEach((c, i) => {
        if (i === 0) {
          out = out.concat(toAtoms(c));
          return;
        }
        if (c.k === 'n' && c.v! < 0) {
          out.push('−');
          out = out.concat(numAtoms(-c.v!));
        } else if (c.k === 'neg') {
          out.push('−');
          out = out.concat(negOperand(c.a!));
        } else if (c.k === 'mul' && c.c![0]!.k === 'n' && c.c![0]!.v! < 0) {
          out.push('−');
          out = out.concat(toAtoms(nary('mul', [num(-c.c![0]!.v!)].concat(c.c!.slice(1)))));
        } else {
          // « 1 + (−40) ÷ 100 » : un terme qui commence par « − » après « + » prend des parenthèses
          const a = toAtoms(c);
          out.push('+');
          out = out.concat(a[0] === '−' ? ['('].concat(a).concat([')']) : a);
        }
      });
      return out;
    }
    case 'mul': {
      // −2 × x s'écrit −2x (comme l'élève, et comme la ligne relue)
      if (t.c![0]!.k === 'n' && t.c![0]!.v! < 0)
        return ['−'].concat(toAtoms(nary('mul', [num(-t.c![0]!.v!)].concat(t.c!.slice(1)))));
      let o: string[] = [];
      t.c!.forEach((c, i) => {
        const prev = t.c![i - 1];
        const implicit =
          prev &&
          ((prev.k === 'n' &&
            prev.v! >= 0 &&
            (c.k === 's' || c.k === 'add' || (c.k === 'sq' && c.a!.k === 's'))) ||
            (prev.k === 'add' && c.k === 'add') ||
            (prev.k === 's' && c.k === 'add') ||
            // jamais deux lettres collées : « ab », « Ll », « xx » se lisent comme UNE grandeur
            false);
        if (i > 0 && !implicit) o.push('×');
        o = o.concat(
          wrap(
            c,
            c.k === 'add' ||
              c.k === 'neg' ||
              (c.k === 'n' && c.v! < 0 && i > 0) ||
              // (L ÷ c) × (l ÷ c) : sans parenthèses, la ligne relue serait ((L ÷ c) × l) ÷ c
              (c.k === 'div' && i > 0) ||
              // (2 parmi 4) × (3 parmi 28) : lisible (moteur v27)
              c.k === 'binom',
          ),
        );
      });
      return o;
    }
    case 'div':
      return wrap(t.a!, t.a!.k === 'add' || t.a!.k === 'neg' || t.a!.k === 'binom')
        .concat(['÷'])
        .concat(wrap(t.b!, !isAtomic(t.b!) && t.b!.k !== 'sq' && t.b!.k !== 'pct'));
    case 'pow': {
      const at = powAtom(t);
      if (at) return [at];
      // puissance générale (moteur v2) : 1,05^n, q^(n − 1) ; (2³)^33 garde ses parenthèses
      return wrap(t.a!, !isAtomic(t.a!) || t.a!.k === 'pow')
        .concat(['^'])
        .concat(wrap(t.b!, !isAtomic(t.b!)));
    }
    case 'neg':
      return ['−'].concat(negOperand(t.a!));
    case 'sq':
      // (10³)² : sans parenthèses, « 10³² » se relirait 10³²
      return wrap(t.a!, !isAtomic(t.a!) || t.a!.k === 'pow').concat(['²']);
    case 'pct':
      return wrap(t.a!, !isAtomic(t.a!)).concat(['%']);
    case 'deg':
      return wrap(t.a!, !isAtomic(t.a!)).concat(['°']);
    case 'fact':
      return wrap(t.a!, !isAtomic(t.a!)).concat(['!']);
    case 'binom':
      // « (k − 1) parmi (n − 1) » ; un nombre négatif ou une opération entre parenthèses
      return wrap(t.a!, !isAtomic(t.a!))
        .concat(['parmi'])
        .concat(wrap(t.b!, !isAtomic(t.b!)));
    case 'sqrt':
      return ['√'].concat(wrap(t.a!, !isAtomic(t.a!)));
    case 'abs':
      return ['|'].concat(toAtoms(t.a!)).concat(['|']);
    case 'arg':
    case 'ln':
    case 'log':
    case 'exp':
    case 'cos':
    case 'sin':
    case 'tan':
    case 'arccos':
    case 'arcsin':
    case 'arctan':
      // toujours entre parenthèses : « ln(5) », « cos(π ÷ 3) »
      return ([t.k] as string[]).concat(wrap(t.a!, true));
  }
  return [];
}
export function lineAtoms(line: Line): string[] {
  let out: string[] = [];
  line.dis.forEach((d, i) => {
    if (i) out.push('ou');
    d.sides.forEach((s, j) => {
      if (j) out.push(d.chain ? d.chain[j - 1]! : (d.cmp ?? (d.rels ? d.rels[j - 1]! : '=')));
      out = out.concat(toAtoms(s));
    });
    if (d.mod !== undefined) out.push('[' + d.mod + ']');
  });
  return out;
}
export function atomsText(atoms: string[]): string {
  let out = '';
  // module (moteur v11) : « |3 + 4i| », sans espace contre les barres
  let barOpen = false;
  let prevOpeningBar = false;
  atoms.forEach((a, i) => {
    const p = atoms[i - 1];
    const pp = atoms[i - 2];
    if (a === '÷') {
      out += '/';
      prevOpeningBar = false;
      return;
    }
    const closingBar = a === '|' && barOpen;
    if (i && (prevOpeningBar || closingBar)) {
      out += a;
      prevOpeningBar = false;
      if (a === '|') barOpen = !barOpen;
      return;
    }
    prevOpeningBar = a === '|' && !barOpen;
    if (a === '|') barOpen = !barOpen;
    if (i) {
      // unité (moteur v4) : le bout porte déjà son espace fine (« 3,5 m³ »)
      if (a[0] === '\u2009') out += '';
      else if (
        a === '²' ||
        a === ')' ||
        p === '÷' ||
        a === '^' ||
        p === '^' ||
        a === '°' ||
        a === '!'
      )
        out += '';
      else if (isFunc(p) && a === '(') out += '';
      else if (a === '%')
        out += '\u202F'; // espace fine insécable avant %
      else if (p === '(' || p === '√') out += '';
      else if (
        p === '−' &&
        (pp === undefined || pp === '=' || pp === '(' || pp === 'ou' || /^[+−×÷]$/.test(pp))
      )
        out += '';
      // signe « + » d'un nombre écrit entre parenthèses (moteur v36) : « 6 − (+10) »
      else if (p === '+' && pp === '(') out += '';
      else if (
        (isNum(p) || p === ')' || p === '²') &&
        (isVar(a) || isConst(a) || isFunc(a) || a === '(')
      )
        out += '';
      else if (isVar(p) && a === '(')
        out += ''; // x(x + 3)
      else if (isVar(p) && isVar(a) && p!.length === 1 && a.length === 1) out += '';
      else out += ' ';
    }
    out += a;
  });
  return out;
}
export function textOf(t: Node): string {
  return atomsText(toAtoms(t));
}
export function lineText(line: Line): string {
  return atomsText(lineAtoms(line));
}

/* ================= complexité ================= */
export function ops(t: Node): number {
  if (t.k === 'n' || t.k === 's') return 0;
  if (powAtom(t) && hasUnknown(t, {})) return 0;
  if (t.c) return t.c.length - 1 + t.c.reduce((s, c) => s + ops(c), 0);
  if (isBin(t)) return 1 + ops(t.a!) + ops(t.b!);
  return 1 + ops(t.a!);
}
/** une parenthèse à développer compte comme du travail restant */
export function parens(t: Node): number {
  const own =
    (t.k === 'mul' && t.c!.some((c) => c.k === 'add')) || (t.k === 'sq' && t.a!.k === 'add')
      ? 2
      : 0;
  return own + kids(t).reduce((s, c) => s + parens(c), 0);
}
export function knownCount(t: Node, K: Env): number {
  if (t.k === 's') return K[t.n!] !== undefined ? 1 : 0;
  return kids(t).reduce((s, c) => s + knownCount(c, K), 0);
}
/** plus c'est petit, plus la relation est « avancée » ; l'inconnue enfouie coûte cher */
export function eqM(eq: Eq, K: Env): number {
  return eq.sides.reduce((m, s) => {
    const o = ops(s) + parens(s);
    return m + o + knownCount(s, K) + (hasUnknown(s, K) ? 10 * o : 0);
  }, 0);
}

/* ================= même relation ? même expression ? ================= */
const SAMPLES = [
  [1.37, 2.11, 0.83],
  [2.9, 0.61, 3.7],
  [0.53, 3.3, 1.9],
  [4.1, 1.7, 2.6],
  [1.13, 4.6, 0.97],
];
function sampleEnv(K: Env, U: string[], i: number): Env {
  const env: Env = Object.assign({}, K);
  U.forEach((u, j) => {
    env[u] = SAMPLES[i]![j % 3]! + j * 0.17;
  });
  return env;
}
export function eqEquiv(a: Eq, b: Eq, K: Env): boolean {
  if (a.sides.length !== 2 || b.sides.length !== 2) return false;
  const sa = Object.keys(symsOf({ k: 'add', c: a.sides })).sort();
  const sb = Object.keys(symsOf({ k: 'add', c: b.sides })).sort();
  if (sa.join() !== sb.join()) return false;
  const U = sa.filter((s) => K[s] === undefined);
  let ratio: number | null = null;
  for (let i = 0; i < SAMPLES.length; i++) {
    const env = sampleEnv(K, U, i);
    const fa = ev(a.sides[0]!, env) - ev(a.sides[1]!, env);
    const fb = ev(b.sides[0]!, env) - ev(b.sides[1]!, env);
    if (!isFinite(fa) || !isFinite(fb)) return false;
    const za = Math.abs(fa) < 1e-9;
    const zb = Math.abs(fb) < 1e-9;
    if (za !== zb) return false;
    if (!za) {
      const r = fa / fb;
      if (ratio === null) ratio = r;
      else if (!close(r, ratio)) return false;
    }
    if (!U.length) break;
  }
  return true;
}
export function lineEquiv(a: Line, b: Line, K: Env): boolean {
  if (a.dis.length !== b.dis.length) return false;
  return permutations(b.dis.map((_, i) => i)).some((perm) =>
    a.dis.every((d, i) => eqEquiv(d, b.dis[perm[i]!]!, K)),
  );
}
/** deux expressions égales quelles que soient les valeurs des inconnues (développer, réduire…) */
export function identical(a: Node, b: Node, K: Env): boolean {
  const U = Object.keys(symsOf({ k: 'add', c: [a, b] })).filter((s) => K[s] === undefined);
  for (let i = 0; i < SAMPLES.length; i++) {
    const env = sampleEnv(K, U, i);
    if (!close(ev(a, env), ev(b, env))) return false;
    if (!U.length) break;
  }
  return true;
}
export function permutations<T>(arr: T[]): T[][] {
  if (arr.length <= 1) return [arr.slice()];
  const res: T[][] = [];
  arr.forEach((x, i) => {
    permutations(arr.slice(0, i).concat(arr.slice(i + 1))).forEach((p) => res.push([x].concat(p)));
  });
  return res;
}

/* ================= appariement =================
   e = ce que l'élève a écrit, f = une ligne qu'on sait justifiée.
   Autorisé : remplacer une grandeur connue par sa valeur, calculer un sous-calcul,
   écrire 6 × 6 pour 6², 20 ÷ 100 pour 20 %, regrouper ou faire disparaître des nombres.
   lo (facultatif) tolère une erreur de calcul pour la repérer. */
export interface MatchCost {
  ev: number;
  sub: number;
}
export interface Lenient {
  mis: number;
  max: number;
  at: { wrote: number; expected: number; f: Node } | null;
}
function Z(): MatchCost {
  return { ev: 0, sub: 0 };
}
export function addM(...ms: MatchCost[]): MatchCost {
  const r = Z();
  ms.forEach((m) => {
    r.ev += m.ev;
    r.sub += m.sub;
  });
  return r;
}
type LoSave = { mis: number; at: Lenient['at'] } | null;
function saveLo(lo?: Lenient | null): LoSave {
  return lo ? { mis: lo.mis, at: lo.at } : null;
}
function restoreLo(lo: Lenient | null | undefined, s: LoSave): void {
  if (lo && s) {
    lo.mis = s.mis;
    lo.at = s.at;
  }
}
/** (−3) × x → −(3 × x), pour comparer avec un « − 3x » lu dans une somme. */
function negCoefForm(t: Node): Node | null {
  if (t.k !== 'mul') return null;
  const c0 = t.c![0]!;
  // (−3) × x : nombre négatif, ou « (−1) » lu comme −(1)
  // (−m) × 2 : −(m × 2)
  if (c0.k === 'neg' && c0.a!.k !== 'n')
    return { k: 'neg', a: nary('mul', [c0.a!, ...t.c!.slice(1)]) };
  const v =
    c0.k === 'n' && c0.v! < 0 ? -c0.v! : c0.k === 'neg' && c0.a!.k === 'n' ? c0.a!.v! : null;
  if (v === null) return null;
  const rest = t.c!.slice(1);
  return { k: 'neg', a: v === 1 && rest.length === 1 ? rest[0]! : nary('mul', [num(v), ...rest]) };
}
export function match(e: Node, f: Node, K: Env, lo?: Lenient | null): MatchCost | null {
  if (e.k === 'n') {
    const fv = numVal(f, K);
    if (fv === null) return null;
    if (!close(fv, e.v!)) {
      if (lo && lo.mis < lo.max && f.k !== 'n') {
        lo.mis++;
        lo.at = { wrote: e.v!, expected: fv, f };
        return Z();
      }
      return null;
    }
    return { ev: f.k === 'n' || f.k === 's' ? 0 : 1, sub: knownCount(f, K) };
  }
  if (e.k === 's') return f.k === 's' && f.n === e.n ? Z() : null;
  // un terme NÉGATIF écrit avec les nombres (« (−1) × 1 », « − 1 × 1 ») face à un
  // produit de grandeurs connues (z₁ × z₂ avec z₁ = −1) : on compare les valeurs —
  // sinon remplacer une valeur négative dans une formule n'était jamais reconnu
  if (
    (e.k === 'neg' || (e.k === 'mul' && negCoefForm(e))) &&
    !Object.keys(symsOf(e)).length &&
    f.k !== 'n' &&
    numericIn(f, K)
  ) {
    const ev0 = numVal(e, K);
    const fv0 = numVal(f, K);
    if (ev0 !== null && fv0 !== null && close(ev0, fv0)) return { ev: 1, sub: knownCount(f, K) };
  }
  // −(3x) et (−3) × x sont la même écriture : « 26 − 3x = 20 » donne « −3x = 20 − 26 »
  const negE = negCoefForm(e);
  const negF = negCoefForm(f);
  if (negE && f.k === 'neg') return match(negE, f, K, lo);
  if (negF && e.k === 'neg') {
    const m = match(e, negF, K, lo);
    // (−1) × 1 écrit « − 1 × 1 » : le 1 gardé comme facteur
    if (m || negF.a!.k === 'mul') return m;
    return match(e, { k: 'neg', a: nary('mul', [num(1), negF.a!]) }, K, lo);
  }
  if (e.k !== f.k) {
    if (f.k === 'sq' && e.k === 'mul' && e.c!.length === 2) {
      const s0 = saveLo(lo);
      const m1 = match(e.c![0]!, f.a!, K, lo);
      const m2 = m1 && match(e.c![1]!, f.a!, K, lo);
      if (m1 && m2) return addM(m1, m2, { ev: 1, sub: 0 });
      restoreLo(lo, s0);
      return null;
    }
    if (f.k === 'pct' && e.k === 'div' && e.b!.k === 'n' && e.b!.v === 100) {
      const m3 = match(e.a!, f.a!, K, lo);
      return m3 ? addM(m3, { ev: 1, sub: 0 }) : null;
    }
    if (f.k === 'add' || f.k === 'mul') return matchNary({ k: f.k, c: [e] }, f, K, lo);
    return null;
  }
  if (e.c) return matchNary(e, f, K, lo);
  if (isBin(e)) {
    const s1 = saveLo(lo);
    const a = match(e.a!, f.a!, K, lo);
    const b = a && match(e.b!, f.b!, K, lo);
    if (a && b) return addM(a, b);
    restoreLo(lo, s1);
    return null;
  }
  return match(e.a!, f.a!, K, lo);
}
function matchNary(e: Node, f: Node, K: Env, lo?: Lenient | null): MatchCost | null {
  const ec = e.c!;
  const fNum: Node[] = [];
  const fSym: Node[] = [];
  f.c!.forEach((c) => (numericIn(c, K) ? fNum : fSym).push(c));
  let best: MatchCost | null = null;
  const used = ec.map(() => false);
  function assignSym(i: number, acc: MatchCost): void {
    if (i === fSym.length) {
      const r = assignNum();
      if (r) {
        const tot = addM(acc, r);
        if (!best || tot.ev < best.ev) best = tot;
      }
      return;
    }
    for (let j = 0; j < ec.length; j++) {
      if (used[j]) continue;
      const save = saveLo(lo);
      const m = match(ec[j]!, fSym[i]!, K, lo);
      if (!m) {
        restoreLo(lo, save);
        continue;
      }
      used[j] = true;
      assignSym(i + 1, addM(acc, m));
      used[j] = false;
      if (best) return;
      restoreLo(lo, save);
    }
  }
  function assignNum(): MatchCost | null {
    const restE = ec.filter((_, j) => !used[j]);
    const n = fNum.length;
    // somme / produit recopié terme à terme dans le même ordre (moteur v8) :
    // « 2 + 2 + 6 + 6 + 4 » face à la même écriture, quelle que soit sa longueur
    if (restE.length === n && n > 4) {
      const save0 = saveLo(lo);
      let same: MatchCost | null = Z();
      for (let k = 0; k < n && same; k++) {
        const mm = match(restE[k]!, fNum[k]!, K, lo);
        same = mm ? addM(same, mm) : null;
      }
      if (same) return same;
      restoreLo(lo, save0);
    }
    const choices = restE.length + 1;
    const total = Math.pow(choices, n);
    // recherche combinatoire bornée (avant v8 : au plus 4 termes côté élève)
    if (n > 8 || total > 200000) return null;
    for (let code = 0; code < total; code++) {
      const groups: Node[][] = restE.map(() => []);
      const drop: Node[] = [];
      let x = code;
      for (let q = 0; q < n; q++) {
        const d = x % choices;
        x = Math.floor(x / choices);
        if (d === restE.length) drop.push(fNum[q]!);
        else groups[d]!.push(fNum[q]!);
      }
      if (drop.length) {
        const dvv = combine(f.k, drop, K);
        if (dvv === null || !close(dvv, f.k === 'add' ? 0 : 1)) continue;
      }
      const save = saveLo(lo);
      let ok: MatchCost | null = { ev: drop.length ? 1 : 0, sub: 0 };
      for (let r = 0; r < restE.length && ok; r++) {
        const g = groups[r]!;
        if (!g.length) {
          ok = null;
          break;
        }
        if (g.length === 1) {
          const mm = match(restE[r]!, g[0]!, K, lo);
          ok = mm ? addM(ok, mm) : null;
        } else {
          if (restE[r]!.k !== 'n') {
            ok = null;
            break;
          }
          const cv = combine(f.k, g, K);
          ok = cv !== null && close(cv, restE[r]!.v!) ? addM(ok, { ev: 1, sub: 0 }) : null;
        }
      }
      if (ok) return ok;
      restoreLo(lo, save);
    }
    return null;
  }
  assignSym(0, Z());
  return best;
}
export function matchEq(e: Eq, f: Eq, K: Env, lo?: Lenient | null): MatchCost | null {
  if (e.sides.length !== 2 || f.sides.length !== 2) return null;
  const save = saveLo(lo);
  let a = match(e.sides[0]!, f.sides[0]!, K, lo);
  let b = a && match(e.sides[1]!, f.sides[1]!, K, lo);
  if (a && b) return addM(a, b);
  restoreLo(lo, save);
  a = match(e.sides[0]!, f.sides[1]!, K, lo);
  b = a && match(e.sides[1]!, f.sides[0]!, K, lo);
  if (a && b) return addM(a, b);
  restoreLo(lo, save);
  return null;
}
export function matchLine(E: Line, F: Line, K: Env, lo?: Lenient | null): MatchCost | null {
  if (E.dis.length !== F.dis.length) return null;
  const perms = permutations(F.dis.map((_, i) => i));
  for (let p = 0; p < perms.length; p++) {
    let acc = Z();
    let ok = true;
    for (let i = 0; i < E.dis.length && ok; i++) {
      const m = matchEq(E.dis[i]!, F.dis[perms[p]![i]!]!, K, lo);
      if (m) acc = addM(acc, m);
      else ok = false;
    }
    if (ok) return acc;
  }
  return null;
}
/** facteurs non constants d'un membre : (x + 3)(x − 5) → [x + 3, x − 5] */
export function topFactors(t: Node, K: Env): Node[] {
  const base = t.k === 'neg' ? t.a! : t;
  const list = base.k === 'mul' ? base.c! : [base];
  return list.filter((c) => hasUnknown(c, K));
}
export function proportional(f: Node, g: Node, K: Env): boolean {
  const U = Object.keys(symsOf({ k: 'add', c: [f, g] })).filter((s) => K[s] === undefined);
  let ratio: number | null = null;
  for (let i = 0; i < SAMPLES.length; i++) {
    const env = sampleEnv(K, U, i);
    const a = ev(f, env);
    const b = ev(g, env);
    if (!isFinite(a) || !isFinite(b) || Math.abs(b) < 1e-12) return false;
    if (ratio === null) ratio = a / b;
    else if (!close(a / b, ratio)) return false;
  }
  return true;
}
/** réécrire sans changer la valeur, oui ; factoriser de tête, non : chaque facteur doit déjà être là */
export function factorOK(from: Node, to: Node, K: Env): boolean {
  const tf = topFactors(to, K);
  if (tf.length <= 1) return true;
  const ff = topFactors(from, K);
  return tf.every((g) => ff.some((f) => proportional(f, g, K)));
}
/** un membre identique, l'autre réécrit sans changer de valeur */
export function identEq(e: Eq, f: Eq, K: Env, loose?: boolean): { from: Node; to: Node } | null {
  if (e.sides.length !== 2 || f.sides.length !== 2) return null;
  for (let sw = 0; sw < 2; sw++) {
    for (let k = 0; k < 2; k++) {
      const a = e.sides[k]!;
      const b = e.sides[1 - k]!;
      const c = f.sides[sw]!;
      const d = f.sides[1 - sw]!;
      if (match(a, c, K) && identical(b, d, K) && (loose || factorOK(d, b, K)))
        return { from: d, to: b };
    }
  }
  return null;
}
